import { Box } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useCallback, useContext, useEffect, useMemo } from 'react'

import dayjs from 'dayjs'
import { CardBox } from '../theme/styleComponents'
import { flexCenter } from '../theme/styleConstants'
import { ActionType } from '../context/AppContextReducer'
import { AppContext, AuthContext } from '../context'
import {
  Entry,
  EntryOrderBy,
  EntryOrderDirection,
  EntryStatus,
  useFetchAllEntriesLazyQuery
} from '../../generated'
import {
  ENTRIES_PAGE_LIMIT,
  ENTRIES_SORT_VALUES,
  FILTER_DATE_FORMAT,
  GRAPHQL_QUERY_POLICY,
  QUERY_PARAMS
} from '../constants'
import { EntryActionType } from '../components/entries/EntryReducer'

import { ContentHead } from '../components/common/ContentHead'
import { EmptyData } from '../components/common/EmptyData'
import EntryFilters from '../components/entries/EntryFilters'
import AllEntriesListing from '../components/entries/AllEntriesListing'
import { EntryContext } from '../context/EntryContext'

const DASHBOARD_TITLE = 'All Entries'

const EntriesDashboardContainer = () => {
  const { currentUser } = useContext(AuthContext)
  const { dispatch: entryDispatch, ...entryState } = useContext(EntryContext)
  const { supervisingUsers } = currentUser || {}
  const supervisingUsersIds = useMemo(() => supervisingUsers?.map(user => user?.id || ''), [supervisingUsers])

  const { dispatch: reducerDispatch, currentPage, isNonClientEntry } = useContext(AppContext)

  const [searchParams] = useSearchParams()
  const getParam = (key: QUERY_PARAMS) => searchParams.get(key)

  const status = getParam(QUERY_PARAMS.STATUS) || QUERY_PARAMS.ALL
  const caseId = getParam(QUERY_PARAMS.MATTER) || QUERY_PARAMS.ALL
  const categoryId = getParam(QUERY_PARAMS.CATEGORY) || QUERY_PARAMS.ALL
  const userId = getParam(QUERY_PARAMS.USER) || QUERY_PARAMS.ALL

  const fromParam = getParam(QUERY_PARAMS.FROM)
  const toParam = getParam(QUERY_PARAMS.TO)
  const validEntriesSortValues: string[] = [
    ENTRIES_SORT_VALUES.MOST_RECENT,
    ENTRIES_SORT_VALUES.ACTIVITY_NEWEST,
    ENTRIES_SORT_VALUES.ACTIVITY_OLDEST
  ]
  const entriesSortParam = getParam(QUERY_PARAMS.ENTRIES_SORT)
  const entriesSort =
    entriesSortParam && validEntriesSortValues.includes(entriesSortParam)
      ? entriesSortParam
      : ENTRIES_SORT_VALUES.MOST_RECENT

  const fromDate = useMemo(
    () =>
      fromParam
        ? dayjs(fromParam!).format(FILTER_DATE_FORMAT)
        : dayjs(new Date()).subtract(1, 'month').format(FILTER_DATE_FORMAT),
    [fromParam]
  )
  const toDate = useMemo(
    () => (toParam ? dayjs(toParam!).format(FILTER_DATE_FORMAT) : dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)),
    [toParam]
  )

  const getTimeEntryStatus = useCallback(() => {
    switch (status?.toLocaleUpperCase()) {
      case EntryStatus.Billed:
        return [EntryStatus.Billed]
      case EntryStatus.Draft:
        return [EntryStatus.Draft]
      case EntryStatus.Unbilled:
        return [EntryStatus.Unbilled]
      default:
        return [EntryStatus.Billed, EntryStatus.Draft, EntryStatus.Unbilled]
    }
  }, [status])

  const { allEntries } = entryState ?? {}

  const [fetchAllEntries, { loading: allLoading, refetch: allRefetch }] = useFetchAllEntriesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    fetchPolicy: 'cache-first',
    variables: undefined,
    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      entryDispatch({ type: EntryActionType.SET_ALL_ENTRIES, allEntries: [] })
    },
    onCompleted(data) {
      const {
        fetchAllEntries: { entries, pagination }
      } = data || {}
      entryDispatch({ type: EntryActionType.SET_ALL_ENTRIES, allEntries: [] })
      if (entries?.length) {
        entryDispatch({ type: EntryActionType.SET_ALL_ENTRIES, allEntries: entries as Entry[] })
      }
      if (pagination) {
        const { totalPages, totalCount } = pagination
        reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages || 1 })
        reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount || 1 })
      }
    }
  })

  const entrySortOrderBy =
    entriesSort === ENTRIES_SORT_VALUES.ACTIVITY_NEWEST || entriesSort === ENTRIES_SORT_VALUES.ACTIVITY_OLDEST
      ? EntryOrderBy.Date
      : EntryOrderBy.CreatedAt
  const entrySortDirection =
    entriesSort === ENTRIES_SORT_VALUES.ACTIVITY_OLDEST ? EntryOrderDirection.Asc : EntryOrderDirection.Desc

  const getEntriesVariables = useCallback(
    () => ({
      fetchTimeEntriesInput: {
        paginationOptions: {
          limit: ENTRIES_PAGE_LIMIT,
          page: currentPage
        },
        filters: {
          status: getTimeEntryStatus(),
          caseId: caseId !== QUERY_PARAMS.ALL ? [caseId] : undefined,
          categoryId: categoryId !== QUERY_PARAMS.ALL ? [categoryId] : undefined,
          userId: userId !== QUERY_PARAMS.ALL ? userId : undefined,
          fromDate,
          toDate
        },
        orderBy: entrySortOrderBy,
        order: entrySortDirection
      }
    }),
    [
      currentPage,
      getTimeEntryStatus,
      caseId,
      categoryId,
      userId,
      fromDate,
      toDate,
      entrySortOrderBy,
      entrySortDirection
    ]
  )

  const handleRefresh = useCallback(() => {
    fetchAllEntries({
      variables: getEntriesVariables(),
      fetchPolicy: 'network-only'
    })
  }, [fetchAllEntries, getEntriesVariables])

  useEffect(() => {
    fetchAllEntries({
      variables: getEntriesVariables()
    })
  }, [
    currentUser?.id,
    currentPage,
    getTimeEntryStatus,
    caseId,
    categoryId,
    userId,
    supervisingUsersIds,
    fromDate,
    toDate,
    entriesSort,
    entrySortOrderBy,
    entrySortDirection,
    fetchAllEntries,
    getEntriesVariables
  ])

  useEffect(() => {
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
  }, [])

  const isEmpty = !allLoading && allEntries?.length === 0

  return (
    <CardBox marginTop={3}>
      <ContentHead title={DASHBOARD_TITLE} />

      <EntryFilters
        isNonClientEntry={isNonClientEntry}
        onRefresh={handleRefresh}
        loading={allLoading}
      />

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title="No Entries!" />
        </Box>
      ) : (
        <AllEntriesListing
          state={entryState}
          entryDispatch={entryDispatch}
          loading={allLoading}
          refetch={allRefetch}
        />
      )}
    </CardBox>
  )
}

export default EntriesDashboardContainer
